import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import { FaChevronDown, FaChevronUp, FaEdit, FaEye } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import { formataDataHoraExibicao, tiposSessoesCampos, formataRespostaRelato } from "../../common/utils";
import ModalEditarMotivoCancelamento from "./ModalEditarMotivoCancelamento";


const renderizaCampos = (valores, t) => {
    if (!Array.isArray(valores) || valores.length === 0) return null;

    const grupos = {};
    valores.forEach(item => {
        const secao = item.formulario_campo__sessao || "0_sem_sessao";
        if (!grupos[secao]) grupos[secao] = [];
        grupos[secao].push(item);
    });

    return Object.entries(grupos).map(([secao, lista]) => {
        const visiveis = lista.filter(c => c.arquivo || (c.valor && c.valor.toString().trim() !== ""));
        if (visiveis.length === 0) return null;

        return (
            <div key={secao} className="row-list pb-0 mb-2">
                {secao !== "0_sem_sessao" && (
                    <h6><strong>{tiposSessoesCampos[secao] || secao}</strong></h6>
                )}
                <Row>
                    {visiveis.map(c => (
                        <Col xs={12} sm={6} key={c.id} className="mb-3">
                            {c.arquivo ? (
                                <Button variant="light" size="sm" href={c.arquivo} target="_blank">
                                    <FaEye className="me-1" /> {t("atendimentos:relatosEtapa.viewFile", { campo: c.formulario_campo__nome })}
                                </Button>
                            ) : (
                                <>
                                    <strong>{c.formulario_campo__nome}:</strong>
                                    <div style={{ whiteSpace: "pre-wrap" }}>
                                        <p className="mb-0">
                                            {formataRespostaRelato(c.valor, c.formulario_campo__tipo, c.valor_exibicao)}
                                        </p>
                                    </div>
                                </>
                            )}
                        </Col>
                    ))}
                </Row>
            </div>
        );
    });
};

const RelatoItem = ({ relato, podeEditarMotivo, onEditarMotivo }) => {
    const { t } = useTranslation(["atendimentos", "common"]);
    const [aberto, setAberto] = useState(false);
    const temValores = Array.isArray(relato.valores_list) && relato.valores_list.length > 0;
    const tituloDefault = relato.tipo === "atendimento"
        ? t("atendimentos:relatosEtapa.stageRecordDefault")
        : t("atendimentos:relatosEtapa.recordDefault");

    return (
        <div className="row-list list-style-2 mb-2 pb-0 row-relato-etapa">
            <Row>
                <Col sm={12}>
                    <h6 className="relato-titulo">{relato.titulo || tituloDefault}</h6>
                    <p className="mb-1">
                        {t("atendimentos:relatosEtapa.date")} <strong>{formataDataHoraExibicao(relato.data)}</strong>
                        {relato.dados_cadastrado_por && "nome" in relato.dados_cadastrado_por
                            ? ` ${t("atendimentos:relatosEtapa.by", {
                                nome: `${relato.dados_cadastrado_por.nome} ${relato.dados_cadastrado_por.sobrenome || ""}`,
                            })}`
                            : ""}
                    </p>
                    {relato.motivo_cancelamento && (
                        <p className="mb-1">
                            <strong>{t("atendimentos:relatosEtapa.cancelReason")}</strong> {relato.motivo_cancelamento}
                            {podeEditarMotivo && (
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 ms-2 align-baseline"
                                    title={t("atendimentos:relatosEtapa.editCancelReason")}
                                    onClick={() => onEditarMotivo(relato)}
                                >
                                    <FaEdit /> {t("common:button.edit")}
                                </Button>
                            )}
                        </p>
                    )}
                </Col>
            </Row>
            {aberto && temValores && (
                <div className="relato-valores">
                    {renderizaCampos(relato.valores_list, t)}
                </div>
            )}
            {temValores && (
                <div
                    className="text-center show-hide-relato"
                    onClick={() => setAberto(prev => !prev)}
                    style={{ cursor: "pointer" }}
                >
                    {aberto
                        ? (<>{t("atendimentos:relatosEtapa.hide")} <FaChevronUp /></>)
                        : (<>{t("atendimentos:relatosEtapa.showInfo")} <FaChevronDown /></>)}
                </div>
            )}
        </div>
    );
};

RelatoItem.propTypes = {
    relato: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        titulo: PropTypes.string,
        data: PropTypes.string,
        tipo: PropTypes.string,
        motivo_cancelamento: PropTypes.string,
        dados_cadastrado_por: PropTypes.object,
        valores_list: PropTypes.array,
    }).isRequired,
    podeEditarMotivo: PropTypes.bool,
    onEditarMotivo: PropTypes.func,
};

RelatoItem.defaultProps = {
    podeEditarMotivo: false,
    onEditarMotivo: () => {},
};

const ModalRelatosEtapaAtual = ({
    show,
    onHide,
    etapaId,
    etapaNome,
    atendimentoId,
    podeEditarMotivo,
    motivosCancelamento,
    onMotivoEditado,
}) => {
    const { t } = useTranslation(["atendimentos", "common"]);
    const [loading, setLoading] = useState(false);
    // guarda a chave da mensagem; é traduzida no render
    const [erro, setErro] = useState("");
    const [carregadoPara, setCarregadoPara] = useState(null);
    const [relatos, setRelatos] = useState([]);
    const [relatoMotivoEdicao, setRelatoMotivoEdicao] = useState(null);

    const aplicarMotivoEditado = (relatoId, novoMotivo) => {
        setRelatos(prev => prev.map(r => (
            r.id === relatoId ? { ...r, motivo_cancelamento: novoMotivo } : r
        )));

        // Propaga para quem abriu o modal, senão o histórico atrás dele fica com o
        // motivo antigo até a página ser recarregada.
        onMotivoEditado(relatoId, novoMotivo);
    };

    const carregar = useCallback(() => {
        if (!etapaId || !atendimentoId) return;
        setLoading(true);
        setErro("");
        api.get(`/atendimentos/relatos/?etapa_id=${etapaId}&atendimento_id=${atendimentoId}&limit=500`)
            .then(response => {
                setRelatos(response.data.results || []);
                setCarregadoPara(`${atendimentoId}-${etapaId}`);
                setLoading(false);
            })
            .catch(error => {
                console.error("Erro ao carregar relatos da etapa:", error);
                setErro("atendimentos:relatosEtapa.loadError");
                setLoading(false);
            });
    }, [etapaId, atendimentoId]);

    useEffect(() => {
        if (!show) return;
        const chaveAtual = `${atendimentoId}-${etapaId}`;
        if (carregadoPara !== chaveAtual && !loading) {
            carregar();
        }
    }, [show, etapaId, atendimentoId, carregadoPara, loading, carregar]);

    const relatosEtapa = relatos.filter(r => r.tipo !== "atendimento");
    const relatosFlutuantes = relatos.filter(r => r.tipo === "atendimento");
    const chaveAtual = `${atendimentoId}-${etapaId}`;
    const carregado = carregadoPara === chaveAtual && !loading && !erro;

    return (
        <Modal show={show} onHide={onHide} size="lg" scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    {etapaNome
                        ? t("atendimentos:relatosEtapa.titleWithStage", { etapa: etapaNome })
                        : t("atendimentos:relatosEtapa.title")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading && (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" className="me-2" />
                        {t("atendimentos:relatosEtapa.loading")}
                    </div>
                )}

                {!loading && erro && (
                    <Alert variant="danger">
                        {t(erro)}
                        <div className="mt-2">
                            <Button size="sm" variant="outline-danger" onClick={carregar}>
                                {t("atendimentos:relatosEtapa.retry")}
                            </Button>
                        </div>
                    </Alert>
                )}

                {carregado && (
                    <>
                        <section className="mb-4">
                            <h5 className="mb-3">{t("atendimentos:relatosEtapa.stageRecords")}</h5>
                            {relatosEtapa.length > 0
                                ? relatosEtapa.map(r => (
                                    <RelatoItem
                                        key={r.id}
                                        relato={r}
                                        podeEditarMotivo={podeEditarMotivo}
                                        onEditarMotivo={setRelatoMotivoEdicao}
                                    />
                                ))
                                : <p className="text-muted">{t("atendimentos:relatosEtapa.noStageRecords")}</p>}
                        </section>
                        <section>
                            <h5 className="mb-3">{t("atendimentos:relatosEtapa.occurrences")}</h5>
                            {relatosFlutuantes.length > 0
                                ? relatosFlutuantes.map(r => (
                                    <RelatoItem
                                        key={r.id}
                                        relato={r}
                                        podeEditarMotivo={podeEditarMotivo}
                                        onEditarMotivo={setRelatoMotivoEdicao}
                                    />
                                ))
                                : <p className="text-muted">{t("atendimentos:relatosEtapa.noOccurrences")}</p>}
                        </section>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>{t("common:button.close")}</Button>
            </Modal.Footer>

            <ModalEditarMotivoCancelamento
                show={!!relatoMotivoEdicao}
                onHide={() => setRelatoMotivoEdicao(null)}
                relato={relatoMotivoEdicao}
                motivos={motivosCancelamento}
                onSalvo={aplicarMotivoEditado}
            />
        </Modal>
    );
};

ModalRelatosEtapaAtual.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    etapaId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    etapaNome: PropTypes.string,
    atendimentoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    podeEditarMotivo: PropTypes.bool,
    motivosCancelamento: PropTypes.arrayOf(PropTypes.string),
    onMotivoEditado: PropTypes.func,
};

ModalRelatosEtapaAtual.defaultProps = {
    podeEditarMotivo: false,
    motivosCancelamento: [],
    onMotivoEditado: () => {},
};

export default ModalRelatosEtapaAtual;
